import type { ReactNode } from "react";
import { Link, useInRouterContext } from "react-router-dom";

import type { ConsolationSettings } from "../lib/api";
import "./consolationConfig.css";

export const DEFAULT_CONSOLATION: ConsolationSettings = {
  entry: "first_round",
  group_entry: "all",
  best_n: 8,
  start: "progressive",
  third_place: false,
  include_walkover: false,
};

export function consolationSummary(
  enabled: boolean | undefined,
  settings: ConsolationSettings | null | undefined,
  mode: "elimination" | "groups",
): string {
  if (!enabled) return "sin consolación";
  const s = { ...DEFAULT_CONSOLATION, ...(settings ?? {}) };
  if (mode === "groups") {
    return s.group_entry === "best_n"
      ? `con consolación (los mejores ${s.best_n} que no pasaron)`
      : "con consolación (los que no pasaron la ronda de grupos)";
  }
  return s.entry === "first_two_rounds"
    ? "con consolación (perdedores de 1ª y 2ª ronda)"
    : "con consolación (perdedores de 1ª ronda)";
}

interface Props {
  /** «elimination» = eliminatoria; «groups» = grupos + eliminatoria. */
  mode: "elimination" | "groups";
  enabled: boolean;
  settings: ConsolationSettings | null | undefined;
  disabled: boolean;
  tournamentId: number;
  onToggle: (enabled: boolean) => void;
  onChange: (settings: ConsolationSettings) => void;
}

function Choice({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`cons-choice${active ? " is-active" : ""}`}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Casilla «Cuadro de consolación» y su configuración (maqueta aprobada
 * Formato-Consolacion, Eduardo 2026-10-17). Apagada: solo la casilla. */
export default function ConsolationConfig({
  mode,
  enabled,
  settings,
  disabled,
  tournamentId,
  onToggle,
  onChange,
}: Props) {
  const s: ConsolationSettings = { ...DEFAULT_CONSOLATION, ...(settings ?? {}) };
  const set = (patch: Partial<ConsolationSettings>) => onChange({ ...s, ...patch });
  const inRouter = useInRouterContext();
  const rankingPath = `/admin/tournaments/${tournamentId}/setup/ranking`;

  return (
    <div className={`cons-config form__full${enabled ? " is-on" : ""}`}>
      <label className="cons-config__toggle">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={disabled}
        />
        <span>
          <strong>Cuadro de consolación</strong> — un segundo cuadro para quienes pierden temprano
        </span>
      </label>
      {enabled && (
        <div className="cons-config__body">
          <section className="cons-config__section">
            <strong className="cons-config__heading">
              ¿Quién entra?{" "}
              <span>· {mode === "elimination" ? "torneo de eliminación" : "grupos + eliminatoria"}</span>
            </strong>
            {mode === "elimination" ? (
              <>
                <div className="cons-config__choices">
                  <Choice active={s.entry === "first_round"} disabled={disabled} onClick={() => set({ entry: "first_round" })}>
                    Perdedores de 1ª ronda
                  </Choice>
                  <Choice
                    active={s.entry === "first_two_rounds"}
                    disabled={disabled}
                    onClick={() => set({ entry: "first_two_rounds" })}
                  >
                    Perdedores de 1ª y 2ª ronda
                  </Choice>
                </div>
                <span className="cons-config__hint">
                  Quien tuvo BYE (pasó directo) y pierde en su primer partido: con «1ª ronda» no entra; con «1ª y 2ª
                  ronda» sí.
                </span>
              </>
            ) : (
              <>
                <span className="cons-config__hint">Los que no pasaron la ronda de grupos.</span>
                <div className="cons-config__choices">
                  <Choice active={s.group_entry === "all"} disabled={disabled} onClick={() => set({ group_entry: "all" })}>
                    Todos
                  </Choice>
                  <Choice
                    active={s.group_entry === "best_n"}
                    disabled={disabled}
                    onClick={() => set({ group_entry: "best_n" })}
                  >
                    Los mejores
                  </Choice>
                  {s.group_entry === "best_n" && (
                    <label className="cons-config__number">
                      <span className="sr-only">Cuántos entran</span>
                      <input
                        type="number"
                        min={2}
                        max={128}
                        value={s.best_n}
                        disabled={disabled}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (Number.isFinite(value)) set({ best_n: Math.max(2, Math.min(128, Math.round(value))) });
                        }}
                      />
                    </label>
                  )}
                </div>
                <span className="cons-config__hint">
                  Ordenados por su lugar en el grupo. Con ellos se arma un cuadro nuevo de eliminación.
                </span>
              </>
            )}
          </section>

          <section className="cons-config__section">
            <strong className="cons-config__heading">Formato</strong>
            <div className="cons-config__choices">
              <span className="cons-choice is-active is-static">Eliminación sencilla · tamaño automático</span>
            </div>
            <span className="cons-config__hint">
              Si no son potencia de 2, los mejores sembrados reciben BYE (p. ej. 7 perdedores → cuadro de 8).
            </span>
          </section>

          <section className="cons-config__section">
            <strong className="cons-config__heading">¿Cuándo arranca?</strong>
            <div className="cons-config__choices">
              <Choice active={mode === "groups" || s.start === "round_complete"} disabled={disabled} onClick={() => set({ start: "round_complete" })}>
                {mode === "elimination" ? "Al terminar toda la 1ª ronda" : "Al terminar los grupos"}
              </Choice>
              {mode === "elimination" && (
                <Choice active={s.start === "progressive"} disabled={disabled} onClick={() => set({ start: "progressive" })}>
                  En cuanto se llenen sus lugares
                </Choice>
              )}
            </div>
            {mode === "elimination" && (
              <span className="cons-config__hint">
                «Se llenen» = un partido de consolación se programa cuando sus dos jugadores ya perdieron.
              </span>
            )}
          </section>

          <section className="cons-config__section">
            <strong className="cons-config__heading">Extras</strong>
            <div className="cons-config__extras">
              <label className="form__checkbox">
                <input
                  type="checkbox"
                  checked={s.third_place}
                  disabled={disabled}
                  onChange={(e) => set({ third_place: e.target.checked })}
                />
                3er lugar de consolación
              </label>
              {mode === "elimination" && (
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    checked={s.include_walkover}
                    disabled={disabled}
                    onChange={(e) => set({ include_walkover: e.target.checked })}
                  />
                  Incluir a quien perdió por W.O.
                </label>
              )}
              <span className="cons-config__fixed">✓ Al perder en consolación ya no hay más partidos</span>
            </div>
          </section>

          <div className="cons-config__notes">
            <span>
              Los puntos de consolación se configuran en tu sistema de ranking ·{" "}
              {inRouter ? <Link to={rankingPath}>Ir a Ranking</Link> : <a href={rankingPath}>Ir a Ranking</a>}
            </span>
            <span>Incluida en la inscripción, sin costo extra.</span>
            <span>La duración de los partidos se captura con el marcador normal de Resultados.</span>
          </div>
        </div>
      )}
    </div>
  );
}
